import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import { applyCapTrack, applyCapDayAll, applyWorkYourWeakness, getCapWeek } from "../api/capApi";
import CoachInfoModal from "./CoachInfoModal";
import {
    addDaysToDateKey,
    formatDateKeyDisplay,
    getAffiliateTimeZone,
    getDateKeyInTimeZone,
    getWeekStartKey,
} from "../utils/affiliateTimezone";

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const normalizeDateLabel = (label) => String(label || "").replace(/\//g, ".");

const formatText = (value) => (value || "").trim() || "No info";

const TrackCard = ({ title, text, scoreType, onApply, onApplyTo, isApplying, isApplied }) => (
    <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent sx={{ pb: "16px !important" }}>
            <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 0.8 }}>
                {title}
            </Typography>
            {scoreType ? (
                <Chip size="small" label={`Score Type: ${scoreType}`} sx={{ mb: 1 }} />
            ) : null}
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {formatText(text)}
            </Typography>
            {typeof onApply === "function" && (
                <Box mt={1.2}>
                    <Stack direction={{ xs: "row", sm: "column" }} spacing={1}>
                        <Button
                            size="small"
                            fullWidth
                            variant="contained"
                            color={isApplied ? "success" : "secondary"}
                            onClick={onApply}
                            disabled={isApplying || !String(text || "").trim()}
                        >
                            {isApplied ? "Applied" : "Apply"}
                        </Button>
                        {typeof onApplyTo === "function" ? (
                            <Button
                                size="small"
                                fullWidth
                                variant="outlined"
                                onClick={onApplyTo}
                                disabled={isApplying || !String(text || "").trim()}
                            >
                                Apply To
                            </Button>
                        ) : null}
                    </Stack>
                </Box>
            )}
        </CardContent>
    </Card>
);


export default function CapWeekView({ affiliateId, paymentCountry = "EE", onApplied }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isCompactViewport = useMediaQuery("(max-width:1200px)");
    const timeZone = getAffiliateTimeZone(paymentCountry);
    const [showWeekly, setShowWeekly] = useState(!isCompactViewport);

    const [weekCursor, setWeekCursor] = useState(() =>
        getWeekStartKey(getDateKeyInTimeZone(new Date(), getAffiliateTimeZone(paymentCountry)))
    );
    const formatStoredDate = (value) => formatDateKeyDisplay(getDateKeyInTimeZone(value, timeZone));
    const toInputDateValue = (value) => getDateKeyInTimeZone(value, timeZone);
    const [capWeek, setCapWeek] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [coachModalDay, setCoachModalDay] = useState(null);
    const [selectedDayIndex, setSelectedDayIndex] = useState(0);
    const [applyState, setApplyState] = useState({ loadingKey: "", message: "", type: "" });
    const [applyToState, setApplyToState] = useState({
        open: false,
        day: null,
        sectionKey: "",
        date: "",
        mode: "track",
    });

    useEffect(() => {
        setWeekCursor(getWeekStartKey(getDateKeyInTimeZone(new Date(), timeZone)));
    }, [timeZone]);

    useEffect(() => {
        const loadWeekData = async () => {
            setLoading(true);
            setError("");
            try {
                const data = await getCapWeek({ weekStart: weekCursor, affiliateId });
                setCapWeek(data);
                setSelectedDayIndex(0);
            } catch (err) {
                if (err?.response?.status === 404) {
                    setCapWeek(null);
                } else {
                    setError(err?.response?.data?.error || "Failed to load CAP data.");
                    setCapWeek(null);
                }
            } finally {
                setLoading(false);
            }
        };
        loadWeekData();
    }, [weekCursor, affiliateId]);

    useEffect(() => {
        setShowWeekly(!isCompactViewport);
    }, [isCompactViewport]);

    const dayMap = useMemo(() => {
        const map = new Map();
        const days = capWeek?.parsedData?.days || [];
        days.forEach((day) => map.set(day.dayName, day));
        return map;
    }, [capWeek]);

    const handleApplyTrack = async (day, sectionKey, targetDate = "") => {
        if (!affiliateId || !capWeek?.weekStart || !day?.dayName) {
            setApplyState({ loadingKey: "", type: "error", message: "Missing CAP apply context." });
            return;
        }

        const loadingKey = `${day.dayName}-${sectionKey}`;
        setApplyState({ loadingKey, type: "", message: "" });
        try {
            const response = await applyCapTrack({
                affiliateId,
                weekStart: capWeek.weekStart,
                dayName: day.dayName,
                sectionKey,
                ...(targetDate ? { targetDate } : {}),
            });

            setApplyState({
                loadingKey: "",
                type: "success",
                message: response?.message || `${sectionKey} applied successfully.`
            });
            setCapWeek((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    appliedSections: {
                        ...(prev.appliedSections || {}),
                        [day.dayName]: {
                            rx: false,
                            intermediate: false,
                            beginner: false,
                            skillWork: false,
                            stretching: false,
                            atHome: false,
                            ...((prev.appliedSections || {})[day.dayName] || {}),
                            [sectionKey]: true
                        }
                    }
                };
            });

            if (onApplied) {
                onApplied();
            }
        } catch (err) {
            setApplyState({
                loadingKey: "",
                type: "error",
                message: err?.response?.data?.error || "Failed to apply CAP track."
            });
        }
    };

    const handleApplyDayAll = async (day, targetDate = "") => {
        if (!affiliateId || !capWeek?.weekStart || !day?.dayName) {
            setApplyState({ loadingKey: "", type: "error", message: "Missing CAP apply context." });
            return;
        }
        const dayName = day.dayName;
        const hasAnyTrack = ["rx", "intermediate", "beginner", "skillWork", "stretching", "atHome"].some(
            (k) => day?.tracks?.[k] && String(day.tracks[k]).trim()
        );
        if (!hasAnyTrack) {
            setApplyState({ loadingKey: "", type: "error", message: "No track content for selected day." });
            return;
        }
        setApplyState({ loadingKey: `dayAll-${dayName}`, type: "", message: "" });
        try {
            const response = await applyCapDayAll({
                affiliateId,
                weekStart: capWeek.weekStart,
                dayName,
                ...(targetDate ? { targetDate } : {}),
            });
            setApplyState({
                loadingKey: "",
                type: "success",
                message: response?.message || "All tracks applied successfully."
            });
            setCapWeek((prev) => {
                if (!prev) return prev;
                const applied = {};
                ["rx", "intermediate", "beginner", "skillWork", "stretching", "atHome"].forEach((k) => {
                    if (day?.tracks?.[k] && String(day.tracks[k]).trim()) applied[k] = true;
                });
                return {
                    ...prev,
                    appliedSections: {
                        ...(prev.appliedSections || {}),
                        [dayName]: { ...((prev.appliedSections || {})[dayName] || {}), ...applied }
                    }
                };
            });
            if (onApplied) onApplied();
        } catch (err) {
            setApplyState({
                loadingKey: "",
                type: "error",
                message: err?.response?.data?.error || "Failed to apply CAP tracks."
            });
        }
    };

    const handleApplyWorkYourWeakness = async () => {
        if (!affiliateId || !capWeek?.weekStart) {
            setApplyState({ loadingKey: "", type: "error", message: "Missing CAP apply context." });
            return;
        }

        setApplyState({ loadingKey: "wyw", type: "", message: "" });
        try {
            const response = await applyWorkYourWeakness({
                affiliateId,
                weekStart: capWeek.weekStart
            });

            setApplyState({
                loadingKey: "",
                type: "success",
                message: response?.message || "Work Your Weakness applied successfully."
            });
            setCapWeek((prev) => prev ? ({ ...prev, workYourWeaknessApplied: true }) : prev);

            if (onApplied) {
                onApplied();
            }
        } catch (err) {
            setApplyState({
                loadingKey: "",
                type: "error",
                message: err?.response?.data?.error || "Failed to apply Work Your Weakness."
            });
        }
    };

    const openApplyToDialog = (day, sectionKey = "", mode = "track") => {
        setApplyToState({
            open: true,
            day,
            sectionKey,
            date: toInputDateValue(day?.date),
            mode,
        });
    };

    const closeApplyToDialog = () => {
        setApplyToState({
            open: false,
            day: null,
            sectionKey: "",
            date: "",
            mode: "track",
        });
    };

    const confirmApplyTo = async () => {
        const targetDate = applyToState.date;
        if (!targetDate) {
            setApplyState({ loadingKey: "", type: "error", message: "Please select target date." });
            return;
        }
        const selectedDay = applyToState.day;
        closeApplyToDialog();
        if (applyToState.mode === "dayAll") {
            await handleApplyDayAll(selectedDay, targetDate);
            return;
        }
        await handleApplyTrack(selectedDay, applyToState.sectionKey, targetDate);
    };

    const renderDayCard = (index, showDayHeader = true) => {
        const dayName = DAY_ORDER[index];
        const day = dayMap.get(dayName);
        const formattedDate = day?.date ? formatStoredDate(day.date) : "";
        const applied = capWeek?.appliedSections?.[dayName] || {};
        const tracksWithContent = ["rx", "intermediate", "beginner", "skillWork", "stretching", "atHome"].filter(
            (k) => day?.tracks?.[k] && String(day.tracks[k]).trim()
        );
        const allTracksApplied = tracksWithContent.length > 0 && tracksWithContent.every((k) => applied[k]);

        return (
            <Card sx={{ bgcolor: "background.paper", p: 2, borderRadius: "12px", boxShadow: 3, width: "100%" }}>
                <CardContent>
                    {showDayHeader && (
                        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, color: "primary.main", textAlign: "center" }}>
                            {DAY_SHORT[index]}{formattedDate ? ` - ${formattedDate}` : ""}
                        </Typography>
                    )}
                    <Box mb={1.2} display="flex" flexDirection="column" gap={1}>
                        <Stack direction={isMobile ? "row" : "column"} spacing={1}>
                            <Button
                                size="small"
                                fullWidth
                                variant="contained"
                                color={allTracksApplied ? "success" : "secondary"}
                                onClick={() => handleApplyDayAll(day || { dayName })}
                                disabled={
                                    applyState.loadingKey === `dayAll-${day?.dayName}` ||
                                    tracksWithContent.length === 0
                                }
                            >
                                {applyState.loadingKey === `dayAll-${day?.dayName}` ? "Applying..." : allTracksApplied ? "Applied" : "Apply All"}
                            </Button>
                            <Button
                                size="small"
                                fullWidth
                                variant="outlined"
                                onClick={() => openApplyToDialog(day || { dayName }, "", "dayAll")}
                                disabled={
                                    applyState.loadingKey === `dayAll-${day?.dayName}` ||
                                    tracksWithContent.length === 0
                                }
                            >
                                Apply To
                            </Button>
                        </Stack>
                        <Button
                            size="small"
                            fullWidth
                            variant="outlined"
                            startIcon={<OndemandVideoIcon />}
                            onClick={() => setCoachModalDay(day || { dayName })}
                        >
                            COACH
                        </Button>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "text.primary" }}>
                        {day?.workoutName || "No workout name"}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "secondary.main", mb: 1.2, fontStyle: "italic" }}>
                        {day?.scoreType?.rx || day?.scoreType?.intermediate || day?.scoreType?.beginner
                            ? `Score Type: ${day?.scoreType?.rx || day?.scoreType?.intermediate || day?.scoreType?.beginner}`
                            : "No score type"}
                    </Typography>
                    <Stack spacing={1.1}>
                        <TrackCard
                            title="RX"
                            text={day?.tracks?.rx}
                            scoreType={day?.scoreType?.rx}
                            onApply={() => handleApplyTrack(day, "rx")}
                            onApplyTo={() => openApplyToDialog(day, "rx", "track")}
                            isApplying={applyState.loadingKey === `${day?.dayName}-rx`}
                            isApplied={!!applied.rx}
                        />
                        <TrackCard
                            title="Intermediate"
                            text={day?.tracks?.intermediate}
                            scoreType={day?.scoreType?.intermediate}
                            onApply={() => handleApplyTrack(day, "intermediate")}
                            onApplyTo={() => openApplyToDialog(day, "intermediate", "track")}
                            isApplying={applyState.loadingKey === `${day?.dayName}-intermediate`}
                            isApplied={!!applied.intermediate}
                        />
                        <TrackCard
                            title="Beginner"
                            text={day?.tracks?.beginner}
                            scoreType={day?.scoreType?.beginner}
                            onApply={() => handleApplyTrack(day, "beginner")}
                            onApplyTo={() => openApplyToDialog(day, "beginner", "track")}
                            isApplying={applyState.loadingKey === `${day?.dayName}-beginner`}
                            isApplied={!!applied.beginner}
                        />
                        <TrackCard
                            title="Skill Work"
                            text={day?.tracks?.skillWork}
                            onApply={() => handleApplyTrack(day, "skillWork")}
                            onApplyTo={() => openApplyToDialog(day, "skillWork", "track")}
                            isApplying={applyState.loadingKey === `${day?.dayName}-skillWork`}
                            isApplied={!!applied.skillWork}
                        />
                        <TrackCard
                            title="Stretching"
                            text={day?.tracks?.stretching}
                            onApply={() => handleApplyTrack(day, "stretching")}
                            onApplyTo={() => openApplyToDialog(day, "stretching", "track")}
                            isApplying={applyState.loadingKey === `${day?.dayName}-stretching`}
                            isApplied={!!applied.stretching}
                        />
                        <TrackCard
                            title="At Home"
                            text={day?.tracks?.atHome}
                            onApply={() => handleApplyTrack(day, "atHome")}
                            onApplyTo={() => openApplyToDialog(day, "atHome", "track")}
                            isApplying={applyState.loadingKey === `${day?.dayName}-atHome`}
                            isApplied={!!applied.atHome}
                        />
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const weekEnd = addDaysToDateKey(weekCursor, 6);
    const fallbackWeekLabel = `${formatDateKeyDisplay(weekCursor)} - ${formatDateKeyDisplay(weekEnd)}`;

    return (
        <Box sx={{ width: "100%", pb: { xs: 1, sm: 2 } }}>
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1000,
                    backgroundColor: "background.default",
                    borderBottom: "1px solid rgba(0,0,0,0.12)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.08)"
                }}
            >
                <Box textAlign="center" my={{ xs: 1, sm: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" my={{ xs: 0.5, sm: 1 }}>
                        <IconButton
                            color="primary"
                            onClick={() => {
                                setWeekCursor((prev) => addDaysToDateKey(prev, -7));
                            }}
                            sx={{ bgcolor: "rgba(0, 0, 0, 0.04)" }}
                        >
                            <ArrowBackIosNewIcon />
                        </IconButton>
                        <Typography variant="h4" sx={{ fontWeight: "bold", color: "primary.main" }}>
                            Weekly CAP
                        </Typography>
                        <IconButton
                            color="primary"
                            onClick={() => {
                                setWeekCursor((prev) => addDaysToDateKey(prev, 7));
                            }}
                            sx={{ bgcolor: "rgba(0, 0, 0, 0.04)" }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    </Box>
                    <Box display={{ xs: "none", sm: "flex" }} justifyContent="center" mb={1}>
                        <Box
                            sx={{
                                display: "flex",
                                p: "3px",
                                borderRadius: "10px"
                            }}
                        >
                            <Button
                                size="small"
                                onClick={() => setShowWeekly(false)}
                                sx={{
                                    minWidth: 78,
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                    color: showWeekly ? "#4a4a4a" : "#ffffff",
                                    bgcolor: showWeekly ? "transparent" : "#1f2338",
                                    "&:hover": {
                                        bgcolor: showWeekly ? "rgba(0,0,0,0.05)" : "#1f2338"
                                    }
                                }}
                            >
                                As Day
                            </Button>
                            <Button
                                size="small"
                                onClick={() => setShowWeekly(true)}
                                sx={{
                                    minWidth: 78,
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                    color: showWeekly ? "#ffffff" : "#4a4a4a",
                                    bgcolor: showWeekly ? "#1f2338" : "transparent",
                                    "&:hover": {
                                        bgcolor: showWeekly ? "#1f2338" : "rgba(0,0,0,0.05)"
                                    }
                                }}
                            >
                                As Week
                            </Button>
                        </Box>
                    </Box>
                </Box>

                <Typography textAlign="center" color="text.secondary" sx={{ mb: 1 }}>
                    {normalizeDateLabel(capWeek?.weekLabel || capWeek?.parsedData?.weekLabel || fallbackWeekLabel)}
                </Typography>

                {!showWeekly && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                        p={1}
                        sx={{
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            flexWrap: "nowrap",
                            mb: 1,
                        }}
                    >
                        {DAY_SHORT.map((day, index) => (
                            <Button
                                key={day}
                                variant={selectedDayIndex === index ? "contained" : "outlined"}
                                color={selectedDayIndex === index ? "primary" : "inherit"}
                                onClick={() => setSelectedDayIndex(index)}
                                sx={{
                                    minWidth: { xs: "40px", sm: "80px" },
                                    flexGrow: 1,
                                    fontSize: { xs: "0.75rem", sm: "1rem" },
                                    padding: { xs: "4px", sm: "8px" },
                                }}
                            >
                                {day}
                            </Button>
                        ))}
                    </Box>
                )}
            </Box>

            {!capWeek && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No CAP data for this week.
                </Alert>
            )}
            {applyState.message ? (
                <Alert severity={applyState.type || "info"} sx={{ mb: 2 }}>
                    {applyState.message}
                </Alert>
            ) : null}

            {showWeekly ? (
                <Grid container spacing={2}>
                    {DAY_SHORT.map((day, index) => {
                        const dayName = DAY_ORDER[index];
                        const dayData = dayMap.get(dayName);
                        return (
                            <Grid item xs={12} sm={6} md={1.7} lg={1.7} key={day}>
                                <Box textAlign="center">
                                    <Typography variant="h6" sx={{ fontWeight: "bold", my: 1 }}>
                                        {day}{dayData?.date ? ` - ${formatStoredDate(dayData.date)}` : ""}
                                    </Typography>
                                    {renderDayCard(index, false)}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box sx={{ maxWidth: 760, mx: "auto" }}>
                            {renderDayCard(selectedDayIndex, true)}
                        </Box>
                    </Grid>
                </Grid>
            )}

            <Grid container spacing={2} sx={{ mt: 0.6 }}>
                <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
                        <CardContent>
                            <Typography variant="h5" fontWeight={700} color="primary.main">
                                Work Your Weakness
                            </Typography>
                            <Box mt={1.2} mb={1.2}>
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="contained"
                                    color={capWeek?.workYourWeaknessApplied ? "success" : "secondary"}
                                    onClick={handleApplyWorkYourWeakness}
                                    disabled={applyState.loadingKey === "wyw" || !(capWeek?.parsedData?.workYourWeaknessSections || []).length}
                                >
                                    {capWeek?.workYourWeaknessApplied ? "Applied" : "Apply"}
                                </Button>
                            </Box>
                            <Divider sx={{ my: 1.5 }} />
                            <Stack spacing={2}>
                                {(capWeek?.parsedData?.workYourWeaknessSections || []).map((section, index) => (
                                    <Card key={`${section.title}-${index}`} sx={{ borderRadius: 2, boxShadow: 1 }}>
                                        <CardContent>
                                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                                                {section.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                                                {section.content}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <CoachInfoModal
                day={coachModalDay}
                open={!!coachModalDay}
                onClose={() => setCoachModalDay(null)}
                isMobile={isMobile}
            />
            <Dialog open={applyToState.open} onClose={closeApplyToDialog} fullWidth maxWidth="xs">
                <DialogTitle>Apply CAP to date</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Target date"
                        type="date"
                        value={applyToState.date}
                        onChange={(event) => setApplyToState((prev) => ({ ...prev, date: event.target.value }))}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeApplyToDialog}>Cancel</Button>
                    <Button onClick={confirmApplyTo} variant="contained" disabled={!applyToState.date}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
